<template>
  <div class="col col-12 pt-3 h-100 team-management">
    <div class="row justify-content-between subheader-row px-5">
      <Subheader
        :text="$t('_web_tournament-my-team', 'My team')"
        :size="1.5"
        class="secondary-font"
      />
      <Btn
        v-if="canManageRoaster && !rosterEdit"
        class="btn-accent"
        :label="
          $t('_web_tournament-participants-go-to-roster-link', 'Select roster')
        "
        @click="goToRosterAction"
      />
    </div>
    <template v-if="!rosterEdit">
      <Panel :variant="['transparent']">
        <div class="row w-100 list-wrapper">
          <HeraDummyList
            v-if="isLoading"
            icon-name="dummy/matches"
            :size="210"
            position="center"
            class="flex-grow-1"
          >
            {{
              $t(
                '_web_tournament-create-roster-loading-size',
                'Fetching your current roster...'
              )
            }}
          </HeraDummyList>
          <template v-else>
            <div
              v-for="(member, index) in mySquad"
              :key="`member-${index}`"
              class="d-flex col-6 justify-content-between align-items-center my-3 p-4 cursor-hand player-item"
            >
              <ListItemAccount
                :avatar-props="generateAvatarProps(member)"
                v-bind="{
                  type: member.type,
                  avatar: member.avatar,
                  name: $filters.displayName(member),
                  accountId: member.login,
                  size: 48,
                  approved: member.approved || member.verified,
                  role: !index ? 'captain' : '',
                }"
                class="d-flex align-items-center w-100 mt-2"
              >
                <div slot="action" class="flex-shrink-0 h-mr-auto actions">
                  <Icon
                    v-if="member.login !== myLogin"
                    class="h-ml-3"
                    name="chat-group"
                    @click.prevent="openChat(member)"
                  />
                  <ThreeDots
                    v-if="canManageRoaster"
                    class="h-ml-3"
                    :items="rosterPlayerMenu(member)"
                  />
                </div>
              </ListItemAccount>
            </div>

            <div
              v-for="index in membersPlaceholderSize"
              :key="`player-placeholder-${index}`"
              class="my-2 player-placeholder player-item"
            >
              <ListItemPlaceholder :height="58" />
            </div>
          </template>
        </div> </Panel
    ></template>
    <div v-else class="roster-contatiner">
      <CreateRoster inline @close="onRosterClose" />
    </div>
  </div>
</template>

<script>
import { CLAN_ROLES } from '@/constants/accounts';
import Subheader from '@/components/atoms/Subheader.vue';
import ListItemAccount from '@/components/common/ListItemAccount.vue';
import ListItemPlaceholder from '@/components/common/ListItemEmptyPlaceholder.vue';
import CreateRoster from '@/views/tournaments/player/tournament-registration/CreateRoster';
import HeraDummyList from '@/components/common/HeraDummyList';
import listMixin from '@/mixins/paginatedLists';
import isLoading from '@/mixins/isLoading';
import popoverChat from '@/mixins/popoverChat';
import Btn from '@/components/atoms/HeraFormBtn';
import Panel from '@/components/atoms/Panel';
import Icon from '@/components/atoms/Icon';
import ThreeDots from '@/components/common/ThreeDots';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';

export default {
  name: 'TournamentTeamManagement',
  components: {
    Subheader,
    ListItemAccount,
    ListItemPlaceholder,
    Btn,
    Panel,
    HeraDummyList,
    Icon,
    ThreeDots,
    CreateRoster,
  },
  mixins: [...listMixin, isLoading, popoverChat],
  data() {
    return {
      mySquad: [],
      clan: {},
      rosterEdit: false,
    };
  },
  computed: {
    ...mapGetters({
      battle: 'dynamicBattleModel',
    }),
    ...mapGetters('my/clans', ['getClan']),
    ...mapState('current', ['accountId', 'account']),
    ...mapState('my', { myLogin: 'accountId' }),
    squadSize() {
      return this.account.squadSize;
    },
    squadSizeStatusText() {
      return `${this.mySquad.length}/${this.squadSize} ${this.$t(
        '_web_team-squad-players',
        'players'
      )}`;
    },
    isParticipant() {
      return (
        this.account.participantStatus &&
        this.account.participantStatus !== 'revoked'
      );
    },
    isCaptain() {
      return (
        this.mySquad.findIndex(
          teammate => teammate.login === this.myLogin && teammate.captain
        ) > -1
      );
    },
    needTeam() {
      return this.squadSize > 1;
    },
    membersPlaceholderSize() {
      return this.squadSize - this.mySquad.length;
    },
    canManageRoaster() {
      return (
        this.isParticipant &&
        this.needTeam &&
        (this.isCaptain || this.clan.role != CLAN_ROLES.MEMBER)
      );
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    ...mapActions([
      'setTournamentSquadParticipants',
      'getTournamentSquadParticipants',
      'errorNotify',
    ]),
    ...mapMutations(['setRegistrationTournament']),
    generateAvatarProps(avatar, type) {
      return {
        size: 48,
        type,
        imgUrl: avatar,
      };
    },
    goToRosterAction() {
      this.setRegistrationTournament(this.account._data);
      // this.$router.push({
      //   name: 'tournamentRegistrationCreateRoster',
      //   params: {
      //     ...this.$route.params,
      //     tournamentID: this.accountId,
      //   },
      // });
      this.rosterEdit = true;
    },
    rosterPlayerMenu(player) {
      let actions = [
        {
          label: this.$t('roster-menu-item-delete', 'Delete'),
          action: () => this.removeFromRoster(player),
        },
      ];
      if (!player.captain) {
        actions.unshift({
          label: this.$t('_web_team-squad-promote', 'Promote to captain'),
          action: () => this.promote(player),
        });
      }
      // else {
      //   actions.unshift({
      //     label: this.$t('_web_team-squad-demote', 'Demote'),
      //     action: () => this.demote(),
      //   });
      // }

      return actions;
    },
    openChat(user) {
      this.openPopoverChat(user.login);
    },
    removeFromRoster(member) {
      const squad = this.mySquad
        .filter(user => user.login != member.login)
        .map(user => user.login);
      return this.setTournamentSquadParticipants({
        clanId: this.account.clanId,
        squad,
      }).then(() => {
        this.mySquad = this.mySquad.filter(user => user.login != member.login);
      });
    },
    demote() {
      var squad = this.mySquad.map(user => user.login);
      const [first, ...rest] = squad;
      squad = [...rest, first];

      return this.setTournamentSquadParticipants({
        clanId: this.account.clanId,
        squad,
      }).then(this.fetchData);
    },
    promote(member) {
      var squad = this.mySquad.map(user => user.login);
      squad = squad.filter(item => item != member.login);
      squad.unshift(member.login);

      return this.setTournamentSquadParticipants({
        clanId: this.account.clanId,
        squad,
      }).then(this.fetchData);
    },
    onRosterClose(saved) {
      if (saved) {
        this.fetchData();
      }
      this.rosterEdit = false;
    },
    fetchData() {
      this.setLoading();
      this.setRegistrationTournament(this.account._data);
      this.getTournamentSquadParticipants()
        .then(squad => {
          this.mySquad = squad || [];
        })
        .catch(this.errorNotify)
        .finally(this.unsetLoading);
      this.clan = this.getClan(this.account.clanId);
    },
  },
};
</script>
<style lang="scss">
.team-management {
  .list-wrapper {
    gap: 1rem;

    .player-item {
      border: solid 1px var(--ph-input-borders);
      border-radius: 8px;
      max-width: calc(50% - 0.5rem);
      width: calc(50% - 0.5rem);

      .list-item-title {
        width: unset !important;
        flex: 0 1 auto !important;
        margin: 0 1rem;
      }

      &.player-placeholder {
        border: 0;
      }
    }
  }
}
</style>
